import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '@/lib/axios';

/** 系统名称：侧边栏主标题、登录页主标题、浏览器标签页共用 */
export const SITE_NAME = '再生资源回收管理系统';
/** 公司/主体名称：侧边栏与登录页副标题 */
export const COMPANY_NAME = '再生资源回收管理系统';

interface Settings {
  site_name: string;
  company_name: string;
  contact_phone: string;
  contact_email: string;
  default_daily_wage: string;
  default_paper_commission: string;
  default_black_blue_commission: string;
  default_white_red_commission: string;
  default_banana_box_commission: string;
  project_income_other_name: string;
  sale_price_other: string;
  [key: string]: any;
}

interface SettingsContextType {
  settings: Settings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  site_name: SITE_NAME,
  company_name: COMPANY_NAME,
  contact_phone: '400-888-8888',
  contact_email: 'admin@guoduomei.com',
  default_daily_wage: '150',
  default_paper_commission: '0.10',
  default_black_blue_commission: '0.15',
  default_white_red_commission: '0.30',
  default_banana_box_commission: '0.50',
  project_income_other_name: '其它',
  sale_price_other: '1.0',
};

const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  loading: true,
  refreshSettings: async () => {},
  updateSettings: () => {},
});

export const useSettings = () => useContext(SettingsContext);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      // 检查是否有登录 token
      const token = localStorage.getItem('token');
      if (token) {
        const response = await apiClient.get('/settings');
        if (response.data.success) {
          // 系统名称固定为再生资源回收管理系统；
          // 其余项（联系方式、单价、提成等）沿用后台配置，缺失时回落默认值。
          const updatedData = {
            ...defaultSettings,
            ...response.data.data,
            site_name: SITE_NAME,
            company_name: COMPANY_NAME,
          };
          setSettings(prev => ({ ...prev, ...updatedData }));
        }
      } else {
        // 未登录状态，使用默认值
        setSettings({ ...defaultSettings });
      }
    } catch (error) {
      console.error('获取设置失败:', error);
      // 如果发生错误，使用默认值
      setSettings({ ...defaultSettings });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // 定期刷新设置（每5分钟）
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSettings();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        refreshSettings: fetchSettings,
        updateSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
